/* eslint-disable perfectionist/sort-object-types */
import {
  Configuration,
  CreateRoleDto,
  ErrorResponse,
  Role,
  RolesApi,
  UpdateRoleDto,
  UserTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

// ===== API CLIENT =====

/**
 * Create a roles API instance
 */
const getRolesApi = () => {
  return new RolesApi(
    new Configuration(),
    configuration.PERMISSIONS_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const rolesKeys = {
  all: ['roles'] as const,
  lists: (params?: RoleQueryParams) =>
    [...rolesKeys.all, 'list', { ...params }] as const,
  single: (roleId?: string) =>
    [...rolesKeys.all, 'detail', { roleId }] as const,
};

// ===== API FUNCTIONS =====

interface RoleQueryParams {
  roleType?: UserTypeEnum;
}

/**
 * Fetch all roles with optional type filter
 */
export const fetchRoles = async (params?: RoleQueryParams): Promise<Role[]> => {
  try {
    const response = await getRolesApi().rolesV2ControllerFindAll(
      params?.roleType,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'rolesV2ControllerFindAll',
    });
  }
};

/**
 * Fetch a single role by ID
 */
export const fetchRoleById = async (key: string): Promise<Role> => {
  try {
    const response = await getRolesApi().rolesV2ControllerFindOne(
      key,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'rolesV2ControllerFindOne',
      params: { key },
    });
  }
};

/**
 * Create a new role
 */
export const createRole = async (data: CreateRoleDto): Promise<Role> => {
  try {
    const response = await getRolesApi().rolesV2ControllerCreate(data);

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to create role')
    );
  }
};

/**
 * Update an existing role
 */
export const updateRole = async ({
  data,
  roleId,
}: {
  data: UpdateRoleDto;
  roleId: string;
}): Promise<Role> => {
  try {
    const response = await getRolesApi().rolesV2ControllerUpdate(roleId, data);

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to update role')
    );
  }
};

/**
 * Delete a role
 */
export const deleteRole = async (roleId: string): Promise<void> => {
  try {
    await getRolesApi().rolesV2ControllerRemove(roleId);
  } catch (error) {
    const axiosError = error as AxiosError;

    throw (
      (axiosError.response?.data as ErrorResponse) ||
      new Error('Failed to delete role')
    );
  }
};

// ===== QUERY HOOKS =====

/**
 * Hook for fetching all roles with optional type filter
 */
export const useRoles = (params?: RoleQueryParams) => {
  return useQuery({
    queryFn: () => fetchRoles(params),
    queryKey: rolesKeys.lists(params),
  });
};

/**
 * Hook for fetching a single role by ID
 */
export const useRole = (roleId?: string) => {
  return useQuery({
    queryFn: roleId ? () => fetchRoleById(roleId) : skipToken,
    queryKey: rolesKeys.single(roleId),
  });
};

// ===== MUTATION HOOKS =====

/**
 * Hook for creating a new role
 */
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      // Invalidate the roles list query when a new role is created
      queryClient.invalidateQueries({
        queryKey: rolesKeys.lists(),
      });
    },
  });
};

/**
 * Hook for updating a role
 */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRole,
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific role detail
      queryClient.invalidateQueries({
        queryKey: rolesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: rolesKeys.single(variables.roleId),
      });
    },
  });
};

/**
 * Hook for deleting a role
 */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      // Invalidate the roles list query when a role is deleted
      queryClient.invalidateQueries({
        queryKey: rolesKeys.lists(),
      });
    },
  });
};
